import type { Lang } from "../index";

export interface AiMeetingCopilotCopy {
  seo: { title: string; description: string };
  eyebrow: string;
  h1: string;
  lede: string;
  moment: { heading: string; body1: string; body2: string };
  how: {
    heading: string;
    body: string;
    bullets: { title: string; body: string }[];
  };
  notetakers: { heading: string; body: string; bullets: string[] };
  useCases: {
    heading: string;
    body: string;
    items: { title: string; body: string; href: string; cta: string }[];
  };
  trust: { heading: string; body: string; bullets: string[] };
  cta: { heading: string; body: string; primary: string; secondary: string };
}

const en: AiMeetingCopilotCopy = {
  seo: {
    title: "AI meeting copilot for live English calls",
    description:
      "Minuta detects questions during an English meeting and drafts live answers based on your docs, profile, and transcript so far. No bot joins; transient processing with local file storage.",
  },
  eyebrow: "Use case hub · AI meeting copilot",
  h1: "AI meeting copilot that helps you answer live",
  lede:
    "Minuta listens during the meeting, detects when a question is aimed at you, and suggests an answer based on your documents, profile, and the transcript so far. Not after the call. During it.",
  moment: {
    heading: "The moment",
    body1:
      "The hard part of an English call is not the transcript. It is the four-second gap after someone asks the question that matters: pricing, architecture, scope, risk, timeline, credibility.",
    body2:
      "You know the answer in your first language. You may even have the answer in a PDF or past note. What you need is a clean English version before the conversation moves on.",
  },
  how: {
    heading: "How it works",
    body:
      "Open Minuta before Zoom, Teams, Meet, or any meeting app. Load the documents and notes you want the copilot to know. Then run the call normally.",
    bullets: [
      {
        title: "Live transcript",
        body: "Minuta captures system audio and builds a live English transcript on your computer.",
      },
      {
        title: "Question detection",
        body: "When a question is aimed at you, the copilot flags the moment and uses the transcript so far as context.",
      },
      {
        title: "Grounded answer draft",
        body: "It drafts a short answer from your docs, profile, and prior turns in the call, in language you can actually say out loud.",
      },
    ],
  },
  notetakers: {
    heading: "Why notetakers are too late",
    body:
      "Otter, Fireflies, Tactiq, and similar tools help you after the meeting: transcript, summary, action items. That is useful, but it does not help when the prospect, client, or manager is waiting for your answer now.",
    bullets: [
      "Notetakers help you remember what happened. Minuta helps you respond while it is happening.",
      "A post-call summary cannot rescue the answer you needed to give live.",
      "Minuta still gives you notes after the call, but the core wedge is the in-call copilot.",
    ],
  },
  useCases: {
    heading: "Use cases",
    body:
      "The hub points to the first roles where live English pressure is expensive and specific documents make the answer better.",
    items: [
      {
        title: "Sales engineer discovery calls",
        body: "Handle technical objections and implementation questions with battlecards and one-pagers already loaded.",
        href: "/use-cases/sales-engineer-discovery-call",
        cta: "Sales engineer",
      },
      {
        title: "Freelancer discovery calls",
        body: "Answer rate, scope, and credibility questions in English without underselling the work.",
        href: "/use-cases/freelancer-discovery-call",
        cta: "Freelancer",
      },
      {
        title: "Tech lead 1-on-1s",
        body: "Give calibrated feedback in English when the wording has to be clear, careful, and human.",
        href: "/use-cases/tech-lead-one-on-one",
        cta: "Tech lead",
      },
    ],
  },
  trust: {
    heading: "Trust layer",
    body:
      "The privacy story matters because the copilot is present during sensitive conversations. It is not the headline; it is why you can actually use the product.",
    bullets: [
      "No bot joins the meeting. The other side does not see an extra participant.",
      "Core transcription uses secure transient APIs and the transcript is stored on your device by default.",
      "Optional external services can be used when you enable them, such as account, billing, web search, and some translation flows. We keep that distinction explicit.",
    ],
  },
  cta: {
    heading: "Try it before your next hard English call",
    body: "Download the desktop app, load one doc, and run the next meeting with the copilot beside you.",
    primary: "Download Minuta",
    secondary: "Start free trial",
  },
};

const pt: AiMeetingCopilotCopy = {
  seo: {
    title: "Copiloto de IA para calls em inglês ao vivo",
    description:
      "O Minuta detecta perguntas durante a reunião em inglês e sugere respostas ao vivo com base nos seus docs, perfil e transcript até agora. Sem bot; transcript salvo localmente.",
  },
  eyebrow: "Hub de caso de uso · Copiloto de reunião",
  h1: "Copiloto de IA que te ajuda a responder ao vivo",
  lede:
    "O Minuta escuta durante a reunião, detecta quando uma pergunta é para você e sugere uma resposta com base nos seus documentos, perfil e no transcript até agora. Não depois da call. Durante.",
  moment: {
    heading: "The moment",
    body1:
      "A parte difícil de uma call em inglês não é o transcript. É o intervalo de quatro segundos depois que alguém faz a pergunta que importa: preço, arquitetura, escopo, risco, prazo, credibilidade.",
    body2:
      "Você sabe a resposta na sua língua. Talvez ela esteja até num PDF ou numa nota antiga. O que você precisa é uma versão limpa em inglês antes da conversa seguir.",
  },
  how: {
    heading: "How it works",
    body:
      "Abra o Minuta antes do Zoom, Teams, Meet ou qualquer app de reunião. Carregue os documentos e notas que o copiloto deve conhecer. Depois rode a call normalmente.",
    bullets: [
      {
        title: "Transcript ao vivo",
        body: "O Minuta captura o áudio do sistema e monta um transcript em inglês no seu computador.",
      },
      {
        title: "Detecção de pergunta",
        body: "Quando uma pergunta é para você, o copiloto marca o momento e usa o transcript até ali como contexto.",
      },
      {
        title: "Resposta fundamentada",
        body: "Ele sugere uma resposta curta a partir dos seus docs, perfil e turnos anteriores da call, em inglês que dá para falar em voz alta.",
      },
    ],
  },
  notetakers: {
    heading: "Why notetakers are too late",
    body:
      "Otter, Fireflies, Tactiq e ferramentas parecidas ajudam depois da reunião: transcript, resumo, action items. Isso é útil, mas não ajuda quando prospect, cliente ou manager está esperando sua resposta agora.",
    bullets: [
      "Notetaker ajuda você a lembrar o que aconteceu. Minuta ajuda você a responder enquanto acontece.",
      "Resumo pós-call não salva a resposta que você precisava dar ao vivo.",
      "O Minuta ainda entrega notas depois da call, mas o wedge central é o copiloto durante a reunião.",
    ],
  },
  useCases: {
    heading: "Use cases",
    body:
      "O hub aponta para os primeiros papéis onde pressão em inglês ao vivo custa caro e documentos específicos melhoram a resposta.",
    items: [
      {
        title: "Discovery call de sales engineer",
        body: "Responda objeções técnicas e perguntas de implementação com battlecards e one-pagers já carregados.",
        href: "/use-cases/sales-engineer-discovery-call",
        cta: "Sales engineer",
      },
      {
        title: "Discovery call de freelancer",
        body: "Responda perguntas de rate, escopo e credibilidade em inglês sem se subvalorizar.",
        href: "/use-cases/freelancer-discovery-call",
        cta: "Freelancer",
      },
      {
        title: "1-on-1 de tech lead",
        body: "Dê feedback calibrado em inglês quando a frase precisa ser clara, cuidadosa e humana.",
        href: "/use-cases/tech-lead-one-on-one",
        cta: "Tech lead",
      },
    ],
  },
  trust: {
    heading: "Trust",
    body:
      "A história de privacidade importa porque o copiloto está presente em conversas sensíveis. Não é a headline; é o motivo pelo qual você consegue usar o produto de verdade.",
    bullets: [
      "Nenhum bot entra na reunião. O outro lado não vê participante extra.",
      "A transcrição principal usa APIs transientes seguras e o transcript fica salvo no seu dispositivo por padrão.",
      "Serviços externos opcionais podem ser usados quando você ativa, como conta, cobrança, busca web e alguns fluxos de tradução. Mantemos essa diferença explícita.",
    ],
  },
  cta: {
    heading: "Teste antes da próxima call difícil ao vivo",
    body: "Baixe o app desktop, carregue um doc e entre na próxima reunião com o copiloto do lado.",
    primary: "Baixar Minuta",
    secondary: "Começar trial grátis",
  },
};

const es: AiMeetingCopilotCopy = {
  seo: {
    title: "Copiloto de IA para calls en inglés en vivo",
    description:
      "Minuta detecta preguntas durante la reunión en inglés y sugiere respuestas en vivo basadas en tus docs, perfil y transcript hasta ahora. Sin bot; transcript guardado localmente.",
  },
  eyebrow: "Hub de caso de uso · Copiloto de reunión",
  h1: "Copiloto de IA que te ayuda a responder en vivo",
  lede:
    "Minuta escucha durante la reunión, detecta cuando una pregunta va para ti y sugiere una respuesta basada en tus documentos, perfil y el transcript hasta ahora. No después de la call. Durante.",
  moment: {
    heading: "The moment",
    body1:
      "La parte difícil de una call en inglés no es el transcript. Es el intervalo de cuatro segundos después de que alguien hace la pregunta que importa: precio, arquitectura, scope, riesgo, plazo, credibilidad.",
    body2:
      "Sabes la respuesta en tu idioma. Quizás incluso está en un PDF o una nota anterior. Lo que necesitas es una versión limpia en inglés antes de que la conversación siga.",
  },
  how: {
    heading: "How it works",
    body:
      "Abre Minuta antes de Zoom, Teams, Meet o cualquier app de reunión. Carga los documentos y notas que el copiloto debe conocer. Luego corre la call normalmente.",
    bullets: [
      {
        title: "Transcript en vivo",
        body: "Minuta captura el audio del sistema y arma un transcript en inglés en tu computadora.",
      },
      {
        title: "Detección de pregunta",
        body: "Cuando una pregunta va para ti, el copiloto marca el momento y usa el transcript hasta ahí como contexto.",
      },
      {
        title: "Respuesta fundamentada",
        body: "Sugiere una respuesta corta desde tus docs, perfil y turnos anteriores de la call, en inglés que sí puedes decir en voz alta.",
      },
    ],
  },
  notetakers: {
    heading: "Why notetakers are too late",
    body:
      "Otter, Fireflies, Tactiq y herramientas similares ayudan después de la reunión: transcript, resumen, action items. Eso sirve, pero no ayuda cuando el prospect, cliente o manager está esperando tu respuesta ahora.",
    bullets: [
      "Un notetaker te ayuda a recordar lo que pasó. Minuta te ayuda a responder mientras está pasando.",
      "Un resumen post-call no rescata la respuesta que tenías que dar en vivo.",
      "Minuta también entrega notas después de la call, pero el wedge central es el copiloto durante la reunión.",
    ],
  },
  useCases: {
    heading: "Use cases",
    body:
      "El hub apunta a los primeros roles donde la presión en inglés en vivo cuesta caro y documentos específicos mejoran la respuesta.",
    items: [
      {
        title: "Discovery call de sales engineer",
        body: "Responde objeciones técnicas y preguntas de implementación con battlecards y one-pagers ya cargados.",
        href: "/use-cases/sales-engineer-discovery-call",
        cta: "Sales engineer",
      },
      {
        title: "Discovery call de freelancer",
        body: "Responde preguntas de rate, scope y credibilidad en inglés sin subvalorar tu trabajo.",
        href: "/use-cases/freelancer-discovery-call",
        cta: "Freelancer",
      },
      {
        title: "1-on-1 de tech lead",
        body: "Da feedback calibrado en inglés cuando la frase tiene que ser clara, cuidadosa y humana.",
        href: "/use-cases/tech-lead-one-on-one",
        cta: "Tech lead",
      },
    ],
  },
  trust: {
    heading: "Trust",
    body:
      "La historia de privacidad importa porque el copiloto está presente en conversaciones sensibles. No es el headline; es la razón por la que puedes usar el producto de verdad.",
    bullets: [
      "Ningún bot entra a la reunión. El otro lado no ve un participante extra.",
      "La transcripción principal usa APIs transitorias seguras y el transcript queda guardado en tu dispositivo por defecto.",
      "Servicios externos opcionales pueden usarse cuando los activas, como cuenta, cobro, búsqueda web y algunos flujos de traducción. Mantenemos esa diferencia explícita.",
    ],
  },
  cta: {
    heading: "Pruébalo antes de tu próxima call difícil en vivo",
    body: "Descarga la app de escritorio, carga un doc y entra a la próxima reunión con el copiloto al lado.",
    primary: "Descargar Minuta",
    secondary: "Empezar prueba gratis",
  },
};

export const aiMeetingCopilotCopy: Record<Lang, AiMeetingCopilotCopy> = { en, pt, es };
